import {
  EnterpriseSubTier,
  ProductSubTier,
  ProductTier,
  PurchaseableSubTier,
  PurchaseableTier,
  SubscribablePlanName,
  SubscriptionPlanName,
} from '@sparemin/api-sdk';
import { SubscriptionPeriod } from 'blocks/Pricing/types';

const PURCHASEABLE_SUB_TIERS: Readonly<ProductSubTier[]> = [
  ProductSubTier.PRO_PAID,
  ProductSubTier.PRO_20200922_PAID,
  ProductSubTier.PRO_20211112_PAID,
  ProductSubTier.PRO_20250516_PAID,
  ProductSubTier.PRO_20250818_PAID,
  ProductSubTier.PRO_GROW_20261120_PAID,
  ProductSubTier.BASIC_20200922_PAID,
  ProductSubTier.BASIC_20211112_PAID,
  ProductSubTier.BASIC_20250516_PAID,
  ProductSubTier.BASIC_20250818_PAID,
  ProductSubTier.UNLIMITED_20211112_PAID,
];

const PURCHASEABLE_TIERS: Readonly<ProductTier[]> = [
  ProductTier.BASIC,
  ProductTier.PRO,
  ProductTier.UNLIMITED,
];

const PRO_SUB_TIERS: Readonly<ProductSubTier[]> = [
  ProductSubTier.PRO_PAID,
  ProductSubTier.PRO_TRIAL,
  ProductSubTier.PRO_ADMIN_ASSIGNED,
  ProductSubTier.PRO_FREE_TEMPORARY,
  ProductSubTier.PRO_EDUCATION_CHARITY,
  ProductSubTier.PRO_20200922_PAID,
  ProductSubTier.PRO_20211112_PAID,
  ProductSubTier.PRO_20250516_PAID,
  ProductSubTier.PRO_20250818_PAID,
  ProductSubTier.PRO_GROW_20261120_PAID,
];

const ENTERPRISE_SUB_TIERS: Readonly<ProductSubTier[]> = [
  ProductSubTier.ENTERPRISE,
  ProductSubTier.ENTERPRISE_API_INTEGRATOR,
];

export function isPurchaseableSubTier(
  subTier: ProductSubTier,
): subTier is PurchaseableSubTier {
  return PURCHASEABLE_SUB_TIERS.includes(subTier);
}

export function isPurchaseableTier(
  tier: ProductTier,
): tier is PurchaseableTier {
  return PURCHASEABLE_TIERS.includes(tier);
}

export function isFreeSubTier(subTier: ProductSubTier): boolean {
  return subTier === ProductSubTier.FREE;
}

export function isProSubTier(subTier: ProductSubTier): boolean {
  return PRO_SUB_TIERS.includes(subTier);
}

export function isBasicSubTier(subTier: ProductSubTier): boolean {
  return [
    ProductSubTier.BASIC_20200922_PAID,
    ProductSubTier.BASIC_20211112_PAID,
    ProductSubTier.BASIC_20250516_PAID,
    ProductSubTier.BASIC_20250818_PAID,
  ].includes(subTier);
}

export function isUnlimitedSubTier(subTier: ProductSubTier): boolean {
  return [
    ProductSubTier.UNLIMITED_20211112_PAID,
    ProductSubTier.UNLIMITED_ADMIN_ASSIGNED,
  ].includes(subTier);
}

export function isEnterpriseSubTier(
  subTier: ProductSubTier,
): subTier is EnterpriseSubTier {
  return ENTERPRISE_SUB_TIERS.includes(subTier);
}

export function isProGrowSubTier(
  subTier: ProductSubTier,
): subTier is EnterpriseSubTier {
  if (!subTier) {
    return false;
  }

  return subTier.startsWith('progrow');
}

export function getSubscriptionPeriod(
  planName: SubscriptionPlanName,
): SubscriptionPeriod {
  if (!planName) {
    return undefined;
  }

  const testValue = planName.toLocaleLowerCase();

  if (testValue.endsWith('monthly')) {
    return 'monthly';
  }

  if (testValue.endsWith('yearly')) {
    return 'yearly';
  }

  return undefined;
}

// downgrading is only available for pro plans
export function getDowngradePlan(
  currentPlanName: SubscriptionPlanName,
): SubscribablePlanName {
  switch (currentPlanName) {
    case SubscriptionPlanName.PRO_20200922_MONTHLY:
    case SubscriptionPlanName.PRO_20211112_MONTHLY:
    case SubscriptionPlanName.PRO_20250516_MONTHLY:
    case SubscriptionPlanName.PRO_20250818_MONTHLY:
    case SubscriptionPlanName.PRO_GROW_20251120_MONTHLY:
    case SubscriptionPlanName.PRO_MONTHLY:
      return SubscriptionPlanName.BASIC_20250818_MONTHLY;

    case SubscriptionPlanName.PRO_20200922_YEARLY:
    case SubscriptionPlanName.PRO_20211112_YEARLY:
    case SubscriptionPlanName.PRO_20250516_YEARLY:
    case SubscriptionPlanName.PRO_20250818_YEARLY:
    case SubscriptionPlanName.PRO_GROW_20251120_YEARLY:
    case SubscriptionPlanName.PRO_YEARLY:
      return SubscriptionPlanName.BASIC_20250818_YEARLY;

    case SubscriptionPlanName.UNLIMITED_20211112_MONTHLY:
      return SubscriptionPlanName.PRO_MONTHLY;

    case SubscriptionPlanName.UNLIMITED_20211112_YEARLY:
      return SubscriptionPlanName.PRO_YEARLY;

    default:
      return undefined;
  }
}

// SPAR:15945 why does this have to exist?
export function getTierFromPlan(name: SubscriptionPlanName): ProductTier {
  switch (name) {
    case SubscriptionPlanName.FREE_FOREVER:
      return ProductTier.FREE;

    case SubscriptionPlanName.PRO_20200922_YEARLY:
    case SubscriptionPlanName.PRO_20200922_MONTHLY:
    case SubscriptionPlanName.PRO_20211112_YEARLY:
    case SubscriptionPlanName.PRO_20211112_MONTHLY:
    case SubscriptionPlanName.PRO_20250516_YEARLY:
    case SubscriptionPlanName.PRO_20250516_MONTHLY:
    case SubscriptionPlanName.PRO_20250818_YEARLY:
    case SubscriptionPlanName.PRO_20250818_MONTHLY:
    case SubscriptionPlanName.PRO_GROW_20251120_MONTHLY:
    case SubscriptionPlanName.PRO_GROW_20251120_YEARLY:
    case SubscriptionPlanName.PRO_ADMIN_ASSIGNED_MANUAL:
    case SubscriptionPlanName.PRO_EDUCATION_CHARITY_FOREVER:
    case SubscriptionPlanName.PRO_MONTHLY:
    case SubscriptionPlanName.PRO_TRIAL_MANUAL:
    case SubscriptionPlanName.PRO_YEARLY:
      return ProductTier.PRO;

    case SubscriptionPlanName.ENTERPRISE_API_INTEGRATOR_FOREVER:
    case SubscriptionPlanName.ENTERPRISE_FOREVER:
      return ProductTier.ENTERPRISE;

    case SubscriptionPlanName.BASIC_20200922_YEARLY:
    case SubscriptionPlanName.BASIC_20200922_MONTHLY:
    case SubscriptionPlanName.BASIC_20211112_YEARLY:
    case SubscriptionPlanName.BASIC_20211112_MONTHLY:
    case SubscriptionPlanName.BASIC_20250516_YEARLY:
    case SubscriptionPlanName.BASIC_20250516_MONTHLY:
    case SubscriptionPlanName.BASIC_20250818_YEARLY:
    case SubscriptionPlanName.BASIC_20250818_MONTHLY:
      return ProductTier.BASIC;

    case SubscriptionPlanName.UNLIMITED_20211112_MONTHLY:
    case SubscriptionPlanName.UNLIMITED_20211112_YEARLY:
      return ProductTier.UNLIMITED;

    default:
      return undefined;
  }
}
